import { useState, useEffect } from 'react';
import type { MediaItem, TagType } from '@/types';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchStorageAsset } from '@/features/search/searchSlice';
import { setPlaying, updateCurrentTime, requestAutoplay } from '@/features/player/playerSlice';
import { toggleTileSelection, setTranscriptSearch } from '@/features/ui/uiSlice';
import { openAssignModal } from '@/features/sections/sectionsSlice';
import { fetchAssetTags, addAssetTag, deleteAssetTag } from '@/features/tags/tagsSlice';
import { useHoverVideoPreview } from '@/hooks/useHoverVideoPreview';
import { msToTimeStr } from '@/services/tabFeedService';
import demoVideo from '@/assets/videos/Image_from_iOS.MOV';
import styles from './MediaTile.module.css';

interface MediaTileProps {
  item: MediaItem;
  variant?: 'grid';
  onRemove?: () => void;
  onPlay?: () => void;
}

export default function MediaTile({ item, variant, onRemove, onPlay }: MediaTileProps) {
  const dispatch = useAppDispatch();
  const { activeAsset, loadingAssetId } = useAppSelector((s) => s.search);
  const selectedIds = useAppSelector((s) => s.ui.selectedTileIds);
  const isSelected = selectedIds.includes(item.id);
  const hasSelection = selectedIds.length > 0;

  const [isFlipped, setIsFlipped] = useState(false);
  const [newTagText, setNewTagText] = useState('');
  const [newTagType, setNewTagType] = useState<TagType>('object');
  const [showAddTag, setShowAddTag] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  // Description add form
  const [showAddDesc, setShowAddDesc] = useState(false);
  const [descText, setDescText] = useState('');
  const [descTimestamp, setDescTimestamp] = useState('');

  const assetTags = useAppSelector((s) => s.tags.assetTags[item.id]);
  const tagsLoading = useAppSelector((s) => s.tags.loadingAssetId === item.id);

  // Fetch tags when card is flipped
  useEffect(() => {
    if (isFlipped) dispatch(fetchAssetTags(item.id));
  }, [isFlipped, item.id, dispatch]);

  const { videoRef, handleMouseEnter, handleMouseLeave, videoError, isVideoVisible } = useHoverVideoPreview(item.storage_url);

  const isLoading = loadingAssetId === item.id;
  const isActive = activeAsset?.asset_id === item.id && !loadingAssetId;

  const playVideo = () => {
    dispatch(setPlaying(false));
    dispatch(updateCurrentTime(0));
    dispatch(requestAutoplay());
    dispatch(setTranscriptSearch(''));
    dispatch(fetchStorageAsset(item.id));
    if (onPlay) {
      onPlay();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTileClick = () => {
    if (isFlipped) return; // back face is showing — don't play
    if (hasSelection) {
      dispatch(toggleTileSelection(item.id));
    } else {
      playVideo();
    }
  };

  const handleCheckbox = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleTileSelection(item.id));
  };

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    playVideo();
  };

  const handleFlip = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFlipped((v) => !v);
  };

  const handleFlipClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFlipped(false);
  };

  const badgeClass =
    item.badge === 'new' ? styles.badgeNew : item.badge === 'hot' ? styles.badgeHot : '';
  const badgeLabel =
    item.badge === 'new' ? 'NEW' : item.badge === 'hot' ? 'TRENDING' : item.badge;

  return (
    <div
      className={`${styles.tile} ${isActive && variant !== 'grid' ? styles.tileActive : ''} ${isSelected ? styles.tileSelected : ''}`}
      onMouseEnter={() => { if (!isFlipped) handleMouseEnter(); }}
      onMouseLeave={() => { handleMouseLeave(); }}
      onClick={handleTileClick}
      role="button"
      tabIndex={0}
      aria-label={`Play ${item.title}`}
      aria-pressed={isSelected}
      onKeyDown={(e) => e.key === 'Enter' && handleTileClick()}
    >
      {/* ── 3D flip wrapper ── */}
      <div className={`${styles.cardInner} ${isFlipped ? styles.cardInnerFlipped : ''}`}>

        {/* ══ FRONT FACE ══ */}
        <div className={styles.thumb}>
        {/* ── Background: thumbnail image or gradient placeholder ── */}
        {item.thumbnail ? (
          <img src={item.thumbnail} className={styles.thumbImg} alt={item.title} loading="lazy" />
        ) : (
          <>
            <div className={`${styles.thumbPlaceholder} ${styles[`grad_${item.gradient}`]}`} />
            <div className={styles.thumbIcon} aria-hidden="true">
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <polygon points="10,9 16,12 10,15" fill="currentColor" stroke="none" />
              </svg>
            </div>
          </>
        )}

        {/* ── Hover video preview ── */}
        <video
          ref={videoRef}
          className={[
            styles.thumbVideo,
            isPortrait ? styles.thumbVideoPortrait : '',
            isVideoVisible && !videoError ? styles.thumbVideoVisible : '',
          ].join(' ')}
          src={item.storage_url ? undefined : demoVideo}
          muted
          loop={!item.storage_url}
          playsInline
          preload={item.storage_url ? 'none' : 'metadata'}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            setIsPortrait(v.videoHeight > v.videoWidth);
          }}
        />

        {/* ── Gradient overlay + always-visible info (bottom of card) ── */}
        {variant !== 'grid' && (
          <div className={styles.bottomOverlay}>
            <div className={styles.bottomTitle}>{item.title}</div>
            <div className={styles.bottomMeta}>
              {item.creator && <span>{item.creator}</span>}
              {item.creator && item.timestamp && <span className={styles.metaDot}>·</span>}
              {item.timestamp && <span>{item.timestamp}</span>}
              {!item.creator && !item.timestamp && item.meta && <span>{item.meta}</span>}
            </div>
          </div>
        )}

        {/* ── Grid variant title overlay ── */}
        {variant === 'grid' && (
          <div className={styles.thumbTitleOverlay}>
            <span className={styles.thumbTitleText}>{item.title}</span>
          </div>
        )}

        {/* ── Category badge (top-left, hidden on hover) ── */}
        {item.category && !isSelected && (
          <span className={`${styles.categoryBadge} ${styles[`cat_${item.category}`]} ${styles.hiddenOnHover}`}>
            {item.category.toUpperCase()}
          </span>
        )}
        {/* Legacy badge */}
        {!item.category && item.badge && !isSelected && (
          <span className={`${styles.badge} ${badgeClass} ${styles.hiddenOnHover}`}>{badgeLabel}</span>
        )}

        {/* ── Duration badge (top-right) ── */}
        {item.duration && (
          <span className={styles.duration}>{item.duration}</span>
        )}

        {/* ── Checkbox — top-left on hover or when selected ── */}
        <button
          className={`${styles.checkbox} ${isSelected ? styles.checkboxSelected : ''}`}
          onClick={handleCheckbox}
          aria-label={isSelected ? 'Deselect clip' : 'Select clip'}
          aria-pressed={isSelected}
        >
          {isSelected ? (
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : null}
        </button>

        {/* ── Hover action buttons (center) ── */}
        <div className={styles.hoverActions} aria-hidden="true">
          <button className={styles.actionBtn} onClick={handlePlay} aria-label="Play video" title="Play">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
          <button className={`${styles.actionBtn} ${styles.actionBtnBlue}`} onClick={handleFlip} aria-label="AI analysis" title="AI Scene Analysis">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
          </button>
          <button
            className={`${styles.actionBtn} ${styles.actionBtnGreen}`}
            onClick={(e) => { e.stopPropagation(); dispatch(openAssignModal(item.id)); }}
            aria-label="Add to collection"
            title="Add to Collection"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
          </button>

        </div>

        {/* ── Hover dark overlay ── */}
        <div className={styles.hoverOverlay} />

        {/* ── Remove from collection button (top-right, shown on hover when onRemove provided) ── */}
        {onRemove && (
          <button
            className={styles.removeBtn}
            title="Remove from collection"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            aria-label="Remove from collection"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            </svg>
          </button>
        )}

        {/* ── Progress bar ── */}
        {item.progress !== undefined && (
          <div className={styles.progress}>
            <div className={styles.progressFill} style={{ width: `${item.progress}%` }} />
          </div>
        )}

        {/* ── Loading spinner ── */}
        {isLoading && (
          <div className={styles.loadingOverlay} aria-label="Loading">
            <div className={styles.spinner} />
          </div>
        )}

        {/* ── Active checkmark (playing) ── */}
        {isActive && variant !== 'grid' && !isSelected && (
          <div className={styles.activeOverlay} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        )}
        </div>{/* end .thumb (front face) */}

        {/* ══ BACK FACE — AI Scene Analysis ══ */}
        <div className={styles.faceBack} onClick={(e) => e.stopPropagation()}>
          {/* Close button */}
          <button className={styles.flipClose} onClick={handleFlipClose} aria-label="Close AI analysis">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M1 1l10 10M11 1L1 11" />
            </svg>
          </button>

          {/* AI badge */}
          <div className={styles.flipHeader}>
            <div className={styles.flipHeaderLeft}>
              <span className={styles.flipPulse} />
              <div>
                <div className={styles.flipTitle}>AI Scene Analysis</div>
              </div>
            </div>
          </div>

          {/* Tags area */}
          <div className={styles.flipTagsArea}>
            {tagsLoading && !assetTags ? (
              <div className={styles.flipTagsLoading}>Loading tags…</div>
            ) : assetTags ? (
              <>
                {/* ── Descriptions block ── */}
                {(assetTags.tags.descriptions?.length ?? 0) > 0 && (
                  <div className={styles.descBlock}>
                    <span className={styles.tagCategoryLabel}>DESCRIPTION</span>
                    {assetTags.tags.descriptions.map((d) => {
                      const ts = d.timestamp_ms ?? null;
                      const text = d.human_value ?? d.ai_value ?? '';
                      return (
                        <div key={d.id ?? text} className={styles.descEntry}>
                          {ts !== null && (
                            <span className={styles.descTimestamp}>{msToTimeStr(ts)}</span>
                          )}
                          <span className={styles.descText}>{text}</span>
                          <button
                            className={styles.descDelete}
                            onClick={(e) => { e.stopPropagation(); dispatch(deleteAssetTag({ assetId: item.id, tagId: d.id ?? '' })); }}
                            aria-label="Remove description"
                            title="Remove"
                          >×</button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Add description form ── */}
                {!showAddDesc ? (
                  <button
                    className={styles.addDescBtn}
                    onClick={(e) => { e.stopPropagation(); setShowAddDesc(true); }}
                  >+ Add Description</button>
                ) : (
                  <div className={styles.addDescForm} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.addDescRow}>
                      <input
                        className={styles.addDescTimestamp}
                        type="text"
                        placeholder="0:00"
                        value={descTimestamp}
                        onChange={(e) => setDescTimestamp(e.target.value)}
                        title="Optional timestamp (e.g. 1:23)"
                      />
                      <span className={styles.addDescTimestampLabel}>optional timestamp</span>
                    </div>
                    <textarea
                      className={styles.addDescTextarea}
                      placeholder="Describe this video…"
                      value={descText}
                      rows={3}
                      onChange={(e) => setDescText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { setShowAddDesc(false); setDescText(''); setDescTimestamp(''); } }}
                    />
                    <div className={styles.addDescActions}>
                      <button
                        className={styles.addTagBtn}
                        onClick={(e) => { e.stopPropagation(); setShowAddDesc(false); setDescText(''); setDescTimestamp(''); }}
                      >Cancel</button>
                      <button
                        className={styles.addTagSave}
                        disabled={!descText.trim()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!descText.trim()) return;
                          // Parse timestamp string "M:SS" or "H:MM:SS" → ms
                          let ts_ms: number | undefined;
                          if (descTimestamp.trim()) {
                            const parts = descTimestamp.trim().split(':').map(Number);
                            if (parts.length === 2) ts_ms = (parts[0] * 60 + parts[1]) * 1000;
                            else if (parts.length === 3) ts_ms = (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
                          }
                          dispatch(addAssetTag({
                            assetId: item.id,
                            payload: { tag_type: 'description', human_value: descText.trim(), source: 'manual', ...(ts_ms !== undefined ? { timestamp_ms_start: ts_ms } : {}) },
                          }));
                          setDescText('');
                          setDescTimestamp('');
                          setShowAddDesc(false);
                        }}
                      >Save</button>
                    </div>
                  </div>
                )}

                {/* ── Other tag categories ── */}
                {(
                  [
                    ['OBJECTS', assetTags.tags.objects],
                    ['ACTIVITIES', assetTags.tags.activities],
                    ['SCENES', assetTags.tags.scenes],
                    ['AI SPEAKERS', assetTags.tags.ai_speakers],
                    ['PEOPLE', assetTags.tags.people],
                    ['TOPICS', assetTags.tags.topics],
                  ] as [string, typeof assetTags.tags.objects][]
                )
                  .filter(([, arr]) => arr && arr.length > 0)
                  .map(([label, arr]) => (
                    <div key={label} className={styles.tagCategory}>
                      <span className={styles.tagCategoryLabel}>{label}</span>
                      <div className={styles.tagGrid}>
                        {arr.map((tag) => (
                          <span key={tag.id} className={styles.tagPill}>
                            {tag.human_value ?? tag.ai_value ?? tag.text}
                            <button
                              className={styles.tagPillDelete}
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch(deleteAssetTag({ assetId: item.id, tagId: tag.id ?? '' }));
                              }}
                              aria-label={`Remove tag ${tag.text}`}
                              title="Remove tag"
                            >×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}

                {/* Add tag toggle */}
                {!showAddTag ? (
                  <button
                    className={styles.addTagBtn}
                    onClick={(e) => { e.stopPropagation(); setShowAddTag(true); }}
                  >+ Add Tag</button>
                ) : (
                  <div className={styles.addTagForm} onClick={(e) => e.stopPropagation()}>
                    <select
                      className={styles.addTagSelect}
                      value={newTagType}
                      onChange={(e) => setNewTagType(e.target.value as TagType)}
                    >
                      <option value="object">Object</option>
                      <option value="activity">Activity</option>
                      <option value="scene">Scene</option>
                      <option value="person">Person</option>
                      <option value="topic">Topic</option>
                      <option value="key_point">Key Point</option>
                      <option value="ai_speaker">AI Speaker</option>
                    </select>
                    <input
                      className={styles.addTagInput}
                      type="text"
                      placeholder="Tag text…"
                      value={newTagText}
                      onChange={(e) => setNewTagText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') setShowAddTag(false); }}
                    />
                    <button
                      className={styles.addTagSave}
                      disabled={!newTagText.trim()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!newTagText.trim()) return;
                        dispatch(addAssetTag({
                          assetId: item.id,
                          payload: { tag_type: newTagType, human_value: newTagText.trim(), source: 'manual' },
                        }));
                        setNewTagText('');
                        setShowAddTag(false);
                      }}
                    >Save</button>
                  </div>
                )}
              </>
            ) : (
              /* fallback: show item.tags as pills */
              <div className={styles.flipSection}>
                <div className={styles.flipSectionLabel}>TAGS</div>
                <div className={styles.tagGrid}>
                  {item.tags.map(t => <span key={t} className={styles.tagPill}>{t}</span>)}
                  {item.tags.length === 0 && <span className={styles.flipTag}>No tags yet</span>}
                </div>
              </div>
            )}
          </div>

          {/* CTA row */}
          <div className={styles.flipCta}>
            <button className={styles.flipCtaPlay} onClick={handlePlay} title="Play clip">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              Play clip
            </button>
            <button
              className={styles.flipCtaAssign}
              onClick={(e) => { e.stopPropagation(); dispatch(openAssignModal(item.id)); }}
              title="Add to collection"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              Add to Collection
            </button>
          </div>
        </div>{/* end .faceBack */}

      </div>{/* end .cardInner */}
    </div>
  );
}
